import React, { useState } from 'react';
import { Flex, Box, Button, Text, Spinner } from '@radix-ui/themes';
import Markdown from 'react-markdown';
import aiService from '../../../services/aiService';
import TopicsPicker from './TopicsPicker';

const LessonNotesGenerator = ({ availableNotes, course, topics, onCancel, onComplete }) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [step, setStep] = useState('pick-topics'); // 'pick-topics' | 'generating' | 'done'

  const handleGenerateNotes = async (topicsToUse) => {
    setLoading(true);
    setError('');
    setStep('generating');
    try {
      const aiResponse = await aiService.generateLessonNotes({
        course_id: course.id,
        subject: course.subject_id,
        level: course.level_id,
        topics: topicsToUse.map(topic => topic.id),
      });
      const jsonMatch = aiResponse.generated.match(/```json\n([\s\S]*?)\n```/);
      const jsonString = jsonMatch ? jsonMatch[1] : null;
      const existingNotes = JSON.parse(sessionStorage.getItem('lessonNotes')) || {};
      const updatedNotes = {
        ...existingNotes,
        [`notes${course.id}`]: {
          content: jsonString,
          generated_at: new Date().toISOString(),
        },
      };
      sessionStorage.setItem('lessonNotes', JSON.stringify(updatedNotes));
      if (!jsonString) {
        throw new Error('Invalid AI response format');
      }
      const parsedJson = JSON.parse(jsonString);
      setNotes(parsedJson);
      setStep('done');
      if (onComplete) onComplete(parsedJson);
    } catch (err) {
      setError('Failed to generate notes. Please try again.');
      setStep('done');
      console.error('Error generating lesson notes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTopicSelection = ({ topics: pickedTopics }) => {
    setSelectedTopics(pickedTopics);
    handleGenerateNotes(pickedTopics);
  };

  const handleResend = () => {
    handleGenerateNotes(selectedTopics);
  };

  if (step === 'pick-topics') {
    return (
      <Box width="100%">
        <TopicsPicker
          onComplete={handleCompleteTopicSelection}
          teachingPlan={course.active_teaching_plan}
          onCancel={onCancel}
          selected={selectedTopics}
        />
      </Box>
    );
  }

  if (loading) {
    return (
      <Flex direction="column" gap="4" align="center" minHeight="290px" justify="center">
        <Spinner size="large" />
        <Text size="1">Generating Notes</Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex direction="column" gap="4" align="center" minHeight="290px" justify="center">
        <Text size="1" color="red">{error}</Text>
        <Button onClick={handleResend}>Try Again</Button>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
      </Flex>
    );
  }

  // Show notes if available
  return (
    <Box width="100%">
      <Text size="2" weight="bold">Generated Lesson Notes</Text>
      <Box mt="2">
        <Markdown>{notes?.content || ''}</Markdown>
      </Box>
      <Flex justify="end" align="center" p="4" gap="2">
        <Button onClick={onCancel} variant="soft">Close</Button>
      </Flex>
    </Box>
  );
};

export default LessonNotesGenerator;
